import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Image,
  Input,
  Link,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiMessageCircle, FiPhone, FiAlertTriangle, FiUpload, FiX } from 'react-icons/fi';
import { db } from '../firebase';
import SpinnerComponent from '../components/Spinner';
import { useAuth } from '../context/AuthContext';
import {
  createPaymentOrder,
  checkBucketHealth,
  correctPaymentUtr,
  getPaymentApiErrorDetails,
  getPaymentStatus,
  submitPaymentUtr,
} from '../services/paymentApi';
import { resolveStoreContext } from '../utils/storeResolver';
import { uploadImageToCloudinary } from '../utils/cloudinaryUpload';

const formatCurrency = (amount) => `₹${(amount || 0).toFixed(2)}`;

const RETRYABLE_PAYMENT_SETUP_CODES = new Set([
  'NO_BUCKET_AVAILABLE',
  'ORDER_TOO_LARGE_FOR_BUCKETS',
  'TRANSACTION_RETRY_EXHAUSTED',
  'SUFFIX_POOL_EXHAUSTED',
  'ORDER_CREATE_ATOMICITY_FAILED',
]);

const getSubdomain = () => {
  const context = resolveStoreContext();
  return (context.type === 'subdomain' || context.type === 'customDomain') ? context.handle || context.domain : null;
};

const OrderPaymentPage = () => {
  const { catalogueId, orderId } = useParams();
  const navigate = useNavigate();
  const { currentUser, storeConfig } = useAuth();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [utrInput, setUtrInput] = useState('');
  const [paymentActionLoading, setPaymentActionLoading] = useState(false);
  const [paymentSetupLoading, setPaymentSetupLoading] = useState(false);
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [paymentProofPreview, setPaymentProofPreview] = useState('');
  const [paymentProofUrl, setPaymentProofUrl] = useState('');
  const [paymentProofUploading, setPaymentProofUploading] = useState(false);
  const [bucketHealthChecked, setBucketHealthChecked] = useState(false);
  const [bucketHealthy, setBucketHealthy] = useState(true);

  const pageBg = useColorModeValue('#F8F9FC', '#09090B');
  const cardBg = useColorModeValue('white', '#111116');
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  const paymentOrderId = order?.paymentOrderId || null;
  const paymentSetupStatus = order?.paymentSetupStatus || (paymentOrderId ? 'READY' : 'PENDING');
  const paymentSetupError = String(order?.paymentIntegrationError || '').trim();
  const paymentSetupErrorCode = String(order?.paymentIntegrationErrorCode || '').trim();
  const paymentSetupRetryable = Boolean(order?.paymentIntegrationRetryable);
  const effectivePaymentStatus = paymentInfo?.paymentStatus || order?.paymentStatus || 'PENDING';
  const canSubmitUtr = ['PENDING', 'UTR_SUBMITTED'].includes(effectivePaymentStatus);
  const canCorrectUtr = ['UTR_SUBMITTED', 'PAYMENT_UNDER_REVIEW'].includes(effectivePaymentStatus);

  // Payment has progressed beyond PENDING — show "Contact Support" instead of cancel
  const paymentInProgress = ['UTR_SUBMITTED', 'PAYMENT_UNDER_REVIEW', 'RECONCILED', 'DISPUTED'].includes(effectivePaymentStatus);

  // Build support contact info from storeConfig
  const supportWhatsapp = storeConfig?.contactWhatsapp || storeConfig?.contactPhone || storeConfig?.phone || '';
  const supportPhone = storeConfig?.contactPhone || storeConfig?.phone || '';

  const resolveStoreHandleForPayment = () => {
    const fromOrder = String(order?.paymentStoreHandle || order?.storeHandle || '').trim().toLowerCase();
    const fromContext = String(getSubdomain() || '').trim().toLowerCase();
    return fromOrder || fromContext;
  };

  const syncPaymentStatus = useCallback(async (paymentOrderIdValue, orderDocId, orderCatalogueId) => {
    if (!paymentOrderIdValue || !currentUser) return;
    try {
      const statusData = await getPaymentStatus(currentUser, paymentOrderIdValue);
      setPaymentInfo(statusData);
      await setDoc(
        doc(db, 'catalogues', orderCatalogueId, 'orders', orderDocId),
        {
          paymentStatus: statusData?.paymentStatus || null,
          paymentUtrNumber: statusData?.utrNumber || null,
          paymentProofUrl: statusData?.paymentProofUrl || null,
          paymentCancelledAtMs: statusData?.cancelledAt || null,
          paymentExpiresAtMs: statusData?.expiresAt || null,
        },
        { merge: true }
      );
      setOrder((prev) => prev ? {
        ...prev,
        paymentStatus: statusData?.paymentStatus || prev.paymentStatus || null,
        paymentUtrNumber: statusData?.utrNumber || prev.paymentUtrNumber || null,
        paymentProofUrl: statusData?.paymentProofUrl || prev.paymentProofUrl || null,
        paymentCancelledAtMs: statusData?.cancelledAt || prev.paymentCancelledAtMs || null,
        paymentExpiresAtMs: statusData?.expiresAt || prev.paymentExpiresAtMs || null,
      } : prev);
      setPaymentProofUrl((prev) => String(statusData?.paymentProofUrl || prev || '').trim());
    } catch (statusError) {
      console.error('Payment status sync failed:', statusError?.response?.data || statusError.message || statusError);
      toast({ title: 'Could not refresh payment status.', status: 'warning', duration: 3000, isClosable: true });
    }
  }, [currentUser, toast]);

  // --- Bucket health validation on load ---
  const runBucketHealthCheck = useCallback(async (paymentOrderIdValue) => {
    if (!paymentOrderIdValue || !currentUser) return;
    try {
      const health = await checkBucketHealth(currentUser, paymentOrderIdValue);
      setBucketHealthChecked(true);
      if (health && !health.healthy) {
        setBucketHealthy(false);
        toast({
          title: 'Payment route has changed',
          description: `Reason: ${health.reason || 'Bucket no longer available'}. Refreshing payment setup...`,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return false;
      }
      setBucketHealthy(true);
      return true;
    } catch (healthError) {
      console.error('Bucket health check failed:', healthError?.response?.data || healthError.message);
      setBucketHealthChecked(true);
      // Don't block the flow on health check failure — let user continue
      return true;
    }
  }, [currentUser, toast]);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      setError('Please log in to continue.');
      return;
    }

    if (!catalogueId || !orderId) {
      setLoading(false);
      setError('Order details are missing.');
      return;
    }

    const fetchOrder = async () => {
      setLoading(true);
      setError(null);
      try {
        const orderRef = doc(db, 'catalogues', catalogueId, 'orders', orderId);
        const orderSnap = await getDoc(orderRef);

        if (!orderSnap.exists()) {
          setError('Order not found.');
          return;
        }

        const orderData = orderSnap.data();
        if (orderData.userId !== currentUser.uid) {
          setError('Access denied for this order.');
          return;
        }

        setOrder({ id: orderSnap.id, ...orderData });
        setPaymentProofUrl(String(orderData?.paymentProofUrl || '').trim());
        setPaymentProofPreview(String(orderData?.paymentProofUrl || '').trim());

        if (orderData.paymentOrderId) {
          await syncPaymentStatus(orderData.paymentOrderId, orderSnap.id, catalogueId);

          // Check bucket health — if unhealthy, auto-retry payment setup
          const isHealthy = await runBucketHealthCheck(orderData.paymentOrderId);
          if (!isHealthy) {
            // Will trigger auto-retry after state is set
          }
        }
      } catch (fetchError) {
        console.error('Fetch payment screen order failed:', fetchError);
        setError('Failed to load payment details.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [catalogueId, currentUser, orderId, syncPaymentStatus, runBucketHealthCheck]);

  const handlePaymentProofSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Screenshot must be under 5MB.', status: 'error', duration: 3000, isClosable: true });
      return;
    }
    const preview = URL.createObjectURL(file);
    setPaymentProofFile(file);
    setPaymentProofPreview(preview);
    setPaymentProofUrl('');
  };

  const handleRemovePaymentProof = () => {
    if (paymentProofPreview && paymentProofPreview.startsWith('blob:')) {
      URL.revokeObjectURL(paymentProofPreview);
    }
    setPaymentProofFile(null);
    setPaymentProofPreview('');
    setPaymentProofUrl('');
  };

  const handlePaymentAction = async (mode) => {
    if (!paymentOrderId || !currentUser) return;

    const normalizedUtr = utrInput.trim();
    if (mode === 'correct' && !/^\d{12}$/.test(normalizedUtr)) {
      toast({ title: 'UTR must be exactly 12 digits.', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    if (mode === 'submit' && normalizedUtr && !/^\d{12}$/.test(normalizedUtr)) {
      toast({ title: 'UTR must be exactly 12 digits when provided.', status: 'warning', duration: 3000, isClosable: true });
      return;
    }

    setPaymentActionLoading(true);
    try {
      if (mode === 'submit') {
        let proofUrlToSend = String(paymentProofUrl || '').trim();
        if (!proofUrlToSend && paymentProofFile) {
          setPaymentProofUploading(true);
          proofUrlToSend = await uploadImageToCloudinary(paymentProofFile);
          setPaymentProofUrl(proofUrlToSend);
          setPaymentProofPreview(proofUrlToSend);
        }
        if (!proofUrlToSend) {
          throw new Error('Payment screenshot is required.');
        }
        await submitPaymentUtr(currentUser, paymentOrderId, normalizedUtr || null, proofUrlToSend);
        toast({ title: 'Payment proof submitted successfully.', status: 'success', duration: 3000, isClosable: true });
      } else if (mode === 'correct') {
        await correctPaymentUtr(currentUser, paymentOrderId, normalizedUtr);
        toast({ title: 'UTR corrected successfully.', status: 'success', duration: 3000, isClosable: true });
      }

      await syncPaymentStatus(paymentOrderId, order.id, catalogueId);
      setUtrInput('');
    } catch (actionError) {
      const message = actionError?.response?.data?.message || actionError.message || 'Payment action failed.';
      toast({ title: message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setPaymentProofUploading(false);
      setPaymentActionLoading(false);
    }
  };

  const handleRetryPaymentSetup = async () => {
    if (!order || !currentUser) return;

    const orderAmount = Number(order?.totalAmount || order?.paymentUniquePayableAmount || 0);
    if (orderAmount <= 0) {
      toast({ title: 'Order amount is invalid for payment setup.', status: 'error', duration: 4000, isClosable: true });
      return;
    }

    const storeHandle = resolveStoreHandleForPayment();
    const orderRef = doc(db, 'catalogues', catalogueId, 'orders', order.id);

    setPaymentSetupLoading(true);
    try {
      const paymentData = await createPaymentOrder(currentUser, {
        buyerId: currentUser.uid,
        orderAmount,
        storeHandle,
      });

      if (!paymentData?.orderId) {
        throw new Error('Payment setup did not return an order id.');
      }

      const setupAt = Timestamp.fromDate(new Date());
      const nextOrderPatch = {
        paymentOrderId: paymentData.orderId,
        paymentStatus: 'PENDING',
        paymentUniquePayableAmount: paymentData.uniquePayableAmount || null,
        paymentQrImageUrl: paymentData.qrImageUrl || null,
        paymentUpiDeepLink: paymentData.upiDeepLink || null,
        paymentExpiresAtMs: paymentData.expiresAt || null,
        paymentCreatedAt: setupAt,
        paymentSetupStatus: 'READY',
        paymentIntegrationError: null,
        paymentIntegrationErrorCode: null,
        paymentIntegrationRetryable: false,
        paymentSetupLastAttemptAt: setupAt,
        paymentStoreHandle: storeHandle || null,
      };

      await setDoc(orderRef, nextOrderPatch, { merge: true });
      setOrder((prev) => prev ? { ...prev, ...nextOrderPatch } : prev);
      setPaymentInfo({ paymentStatus: 'PENDING' });
      setBucketHealthy(true);
      setBucketHealthChecked(true);
      toast({ title: 'Payment setup completed. You can pay now.', status: 'success', duration: 3500, isClosable: true });
    } catch (setupError) {
      const { code, message } = getPaymentApiErrorDetails(setupError);
      const retryable = RETRYABLE_PAYMENT_SETUP_CODES.has(code);

      const failurePatch = {
        paymentSetupStatus: 'FAILED',
        paymentIntegrationError: String(message),
        paymentIntegrationErrorCode: code,
        paymentIntegrationRetryable: retryable,
        paymentSetupLastAttemptAt: Timestamp.fromDate(new Date()),
        paymentStoreHandle: storeHandle || null,
      };

      await setDoc(orderRef, failurePatch, { merge: true });
      setOrder((prev) => prev ? { ...prev, ...failurePatch } : prev);
      toast({
        title: 'Payment setup failed',
        description: message,
        status: retryable ? 'warning' : 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setPaymentSetupLoading(false);
    }
  };

  if (loading) return <SpinnerComponent />;

  return (
    <Box bg={pageBg} minH="100vh" py={{ base: 6, md: 10 }}>
      <Container maxW="container.md">
        <VStack spacing={5} align="stretch">
          <Box p={6} borderWidth="1px" borderColor={borderColor} borderRadius="16px" bg={cardBg} boxShadow="card">
            <HStack justify="space-between" align="start" flexWrap="wrap" spacing={4}>
              <Box>
                <Heading fontSize={{ base: 'lg', md: 'xl' }} color={textColor} fontWeight="800" letterSpacing="-0.02em">
                  Complete Payment
                </Heading>
                <Text fontSize="sm" color={mutedColor} mt={1}>
                  Order ID: {orderId}
                </Text>
                <Text fontSize="xs" color={mutedColor} mt={1}>
                  Setup Status: {paymentSetupStatus}
                </Text>
              </Box>
              <HStack spacing={2}>
                {!paymentOrderId && (
                  <Button size="sm" colorScheme="brand" onClick={handleRetryPaymentSetup} isLoading={paymentSetupLoading}>
                    Retry Payment Setup
                  </Button>
                )}
                <Button size="sm" variant="outline" colorScheme="brand" onClick={() => navigate(`/order-details/${catalogueId}/${orderId}`)}>
                  View Order Details
                </Button>
              </HStack>
            </HStack>
          </Box>

          {error && (
            <Alert status="error" borderRadius="12px">
              <AlertIcon />
              <AlertTitle mr={2}>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Bucket health warning — show if unhealthy and payment is still PENDING */}
          {!error && paymentOrderId && bucketHealthChecked && !bucketHealthy && effectivePaymentStatus === 'PENDING' && (
            <Alert status="warning" borderRadius="12px">
              <AlertIcon />
              <Box>
                <AlertTitle>Payment route needs refresh</AlertTitle>
                <AlertDescription>
                  The collection account assigned to your order is no longer available. Click below to get a fresh payment route.
                </AlertDescription>
                <Button mt={3} size="sm" colorScheme="brand" onClick={handleRetryPaymentSetup} isLoading={paymentSetupLoading}>
                  Refresh Payment Setup
                </Button>
              </Box>
            </Alert>
          )}

          {!error && !paymentOrderId && (
            <Alert status={paymentSetupStatus === 'FAILED' ? 'warning' : 'info'} borderRadius="12px">
              <AlertIcon />
              <Box>
                <AlertTitle>No payment order found</AlertTitle>
                <AlertDescription>
                  Payment setup is currently unavailable for this order. Retry setup to continue with payment.
                </AlertDescription>
                {paymentSetupError && (
                  <Text fontSize="sm" mt={2} color={textColor}>
                    {paymentSetupErrorCode ? `[${paymentSetupErrorCode}] ` : ''}{paymentSetupError}
                  </Text>
                )}
                <HStack mt={3} spacing={2}>
                  <Button size="sm" colorScheme="brand" onClick={handleRetryPaymentSetup} isLoading={paymentSetupLoading}>
                    Retry Payment Setup
                  </Button>
                  {paymentSetupRetryable && (
                    <Badge colorScheme="yellow">Retryable</Badge>
                  )}
                </HStack>
              </Box>
            </Alert>
          )}

          {!error && paymentOrderId && (
            <Box p={6} borderWidth="1px" borderColor={borderColor} borderRadius="16px" bg={cardBg} boxShadow="card">
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between" align="start">
                  <Box>
                    <Text fontSize="xs" fontWeight="700" color={mutedColor} textTransform="uppercase" letterSpacing="0.06em">
                      Payment Tracking
                    </Text>
                    <Text fontSize="xs" color={mutedColor} mt={1}>
                      Payment Order ID: {paymentOrderId}
                    </Text>
                  </Box>
                  <Badge colorScheme={effectivePaymentStatus === 'RECONCILED' ? 'green' : effectivePaymentStatus === 'DISPUTED' ? 'red' : 'blue'}>
                    {effectivePaymentStatus}
                  </Badge>
                </HStack>

                <Text fontSize="sm" color={textColor}>
                  Payable: {formatCurrency(Number(order?.paymentUniquePayableAmount || order?.totalAmount || 0))}
                </Text>

                {order?.paymentQrImageUrl && (
                  <Box>
                    <Text fontSize="xs" color={mutedColor} mb={2}>Scan to Pay</Text>
                    <Image
                      src={order.paymentQrImageUrl}
                      alt="UPI Payment QR"
                      maxW="260px"
                      borderRadius="12px"
                      borderWidth="1px"
                      borderColor={borderColor}
                    />
                  </Box>
                )}

                {order?.paymentUpiDeepLink && (
                  <Link href={order.paymentUpiDeepLink} isExternal color="brand.500" fontWeight="600" w="fit-content">
                    Open UPI App
                  </Link>
                )}

                <FormControl isRequired>
                  <FormLabel fontSize="xs" color={mutedColor}>Payment Screenshot (Required)</FormLabel>
                  {paymentProofPreview ? (
                    <Box position="relative" borderRadius="12px" overflow="hidden" borderWidth="1px" borderColor={borderColor}>
                      <Image src={paymentProofPreview} alt="Payment proof" maxW="280px" borderRadius="12px" />
                      <Button
                        size="xs"
                        colorScheme="red"
                        borderRadius="full"
                        position="absolute"
                        top={2}
                        right={2}
                        onClick={handleRemovePaymentProof}
                      >
                        <FiX />
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      variant="outline"
                      leftIcon={<FiUpload />}
                      onClick={() => document.getElementById('payment-proof-upload').click()}
                      isDisabled={paymentProofUploading}
                    >
                      Upload Screenshot
                    </Button>
                  )}
                  <Input
                    id="payment-proof-upload"
                    type="file"
                    accept="image/*"
                    onChange={handlePaymentProofSelect}
                    display="none"
                  />
                  <Text fontSize="xs" color={mutedColor} mt={1}>
                    Upload screenshot is mandatory. UTR is optional.
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="xs" color={mutedColor}>UTR Number (Optional, 12 digits)</FormLabel>
                  <Input
                    value={utrInput}
                    onChange={(e) => setUtrInput(e.target.value.replace(/\D/g, '').slice(0, 12))}
                    placeholder="Enter UTR (optional)"
                    maxLength={12}
                  />
                </FormControl>

                <HStack spacing={2} flexWrap="wrap">
                  <Button size="sm" colorScheme="blue" onClick={() => handlePaymentAction('submit')} isDisabled={!canSubmitUtr || paymentProofUploading} isLoading={paymentActionLoading || paymentProofUploading}>
                    Submit Payment Proof
                  </Button>
                  <Button size="sm" variant="outline" colorScheme="orange" onClick={() => handlePaymentAction('correct')} isDisabled={!canCorrectUtr} isLoading={paymentActionLoading}>
                    Correct UTR
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => syncPaymentStatus(paymentOrderId, order.id, catalogueId)} isLoading={paymentActionLoading}>
                    Refresh Status
                  </Button>
                </HStack>
              </VStack>
            </Box>
          )}

          {/* Contact Support — shown when payment has progressed beyond PENDING */}
          {paymentInProgress && (
            <Box p={5} borderWidth="1px" borderColor={borderColor} borderRadius="16px" bg={cardBg} boxShadow="card">
              <HStack spacing={2} mb={3}>
                <Icon as={FiAlertTriangle} color="orange.400" boxSize={4} />
                <Text fontSize="xs" fontWeight="700" color={mutedColor} textTransform="uppercase" letterSpacing="0.06em">
                  Need Help?
                </Text>
              </HStack>
              <Text fontSize="sm" color={mutedColor} mb={4}>
                Payment has been initiated. If you need to cancel or have issues, please contact the store directly.
              </Text>
              <Flex gap={3} flexWrap="wrap">
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="brand"
                  onClick={() => navigate('/contact')}
                  leftIcon={<Icon as={FiPhone} />}
                >
                  Contact Support
                </Button>
                {supportWhatsapp && (
                  <Button
                    size="sm"
                    as="a"
                    href={`https://wa.me/${supportWhatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Hi! I need help with my order ${orderId}. Payment status: ${effectivePaymentStatus}.`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    bg="#25D366"
                    color="white"
                    _hover={{ bg: '#1DA851' }}
                    leftIcon={<Icon as={FiMessageCircle} />}
                  >
                    WhatsApp Support
                  </Button>
                )}
              </Flex>
            </Box>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default OrderPaymentPage;